import * as axios from "axios";
import { getAuthHeader, getUploadFileHeader, getSelectedCycleId } from "./../utils";

// Inject the globally-selected appraisal cycle into a request body when the
// caller hasn't already specified one. Lets multiple cycles run in parallel:
// every cycle-scoped write/read is tagged with the cycle the user is working
// in. Callers that pass their own appraisalCycleId/cycleId (e.g. bulk upload,
// or a screen scoped to a specific period) are left untouched. FormData bodies
// are skipped — those endpoints aren't in the cycle-scoped set.
const withSelectedCycle = (model: any) => {
  if (!model || typeof model !== "object" || model instanceof FormData) {
    return model;
  }
  if (model.appraisalCycleId != null || model.cycleId != null) {
    return model;
  }
  const selectedCycleId = getSelectedCycleId();
  if (!selectedCycleId) {
    return model;
  }
  // localStorage holds the id as a string; send it as a number when numeric so
  // the backend gets the expected type.
  const numeric = Number(selectedCycleId);
  return {
    ...model,
    appraisalCycleId: Number.isNaN(numeric) ? selectedCycleId : numeric,
  };
};

export const addAppraisalCycle = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/cycle/add`;

  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      alert(error.response?.data?.error || "Failed to create appraisal cycle");
    }
  );
};

export const getAppraisalCycleList = () => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/cycle/list`;

  return axios.get(url, getAuthHeader()).then(
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      alert(
        error.response?.data?.error ||
          "Failed to fetch appraisal cycle list"
      );
    }
  );
};

export const getAppraisalCycleById = (id: string | number) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/cycle/get/${id}`;

  return axios.get(url, getAuthHeader()).then(
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      alert(
        error.response?.data?.error ||
          "Failed to fetch appraisal cycle detail"
      );
    }
  );
};

export const editAppraisalCycle = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/cycle/update`;

  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      alert(
        error.response?.data?.error || "Failed to update appraisal cycle"
      );
    }
  );
};



export const saveEmployeeAuthority = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-authority/save`;
  model = withSelectedCycle(model);

  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save authority");
    }
  );
};

export const getEmployeeAuthority = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-authority/get`;
  model = withSelectedCycle(model);

  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      alert(
        error.response?.data?.error ||
          "Failed to fetch employee authority details"
      );
    }
  );
};

export const getEmployeeAuthorityDetail = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-authority/detail`;
  model = withSelectedCycle(model);

  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(
        error.response?.data?.error ||
          "Failed to fetch employee authority detail"
      );
    }
  );
};

export const getActiveAppraisalCycle = () => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/cycle/active`;

  return axios.get(url, getAuthHeader()).then(
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      alert(
        error.response?.data?.error ||
          "Failed to fetch active appraisal cycle"
      );
    }
  );
};

// Resolve the cycle a screen should work in: the cycle selected in the topbar
// when one is set (looked up by id so callers get its full object, including
// start/end dates), otherwise the active cycle. Returns the same
// { result: cycle } shape as getActiveAppraisalCycle so call sites are
// interchangeable. Use this on screens that need the whole cycle object;
// screens that only send appraisalCycleId in a request body get it injected
// automatically via withSelectedCycle.
export const getSelectedOrActiveCycle = () => {
  const selectedCycleId = getSelectedCycleId();
  if (selectedCycleId) {
    return getAppraisalCycleById(selectedCycleId);
  }
  return getActiveAppraisalCycle();
};



export const saveKpiReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/save`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save KPI report");
    }
  );
};

export const getKpiReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/get`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch KPI report");
    }
  );
};

export const updateKpiReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update KPI report");
    }
  );
};

export const submitKpiReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/submit`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to submit KPI report");
    }
  );
};

export const approveKpiReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/approve`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to approve KPI report");
    }
  );
};

export const sendBackKpiReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/send-back`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to send back KPI report");
    }
  );
};

export const getSendBackHistory = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/send-back-history`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch send-back history");
    }
  );
};

export const saveSelfAppraisal = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/self-appraisal/save`;
  const formData = new FormData();
  Object.keys(model).forEach((key) => {
    if (model[key] !== null && model[key] !== undefined) {
      formData.append(key, model[key]);
    }
  });
  return axios.post(url, formData, getUploadFileHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save self appraisal");
    }
  );
};

export const getSelfAppraisal = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/self-appraisal/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to get self appraisal");
    }
  );
};

export const updateSelfAppraisal = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/self-appraisal/update`;
  const formData = new FormData();
  Object.keys(model).forEach((key) => {
    if (model[key] !== null && model[key] !== undefined) {
      formData.append(key, model[key]);
    }
  });
  return axios.post(url, formData, getUploadFileHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update self appraisal");
    }
  );
};

export const getAssignedEmployees = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/assigned-employees`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch assigned employees");
    }
  );
};

export const updatePeriodStatus = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/period/update-status`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update period status");
    }
  );
};

export const saveReportingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reporting-assessment/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save reporting assessment");
    }
  );
};

export const getReportingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reporting-assessment/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch reporting assessment");
    }
  );
};

export const updateReportingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reporting-assessment/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update reporting assessment");
    }
  );
};

export const saveKpiTaskGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-task-grade/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save KPI task grades");
    }
  );
};

export const getKpiTaskGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-task-grade/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch KPI task grades");
    }
  );
};

export const updateKpiTaskGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-task-grade/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update KPI task grades");
    }
  );
};

export const getPersonalAttributeMasterList = () => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/personal-attribute-master/list`;
  return axios.get(url, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch personal attribute master list");
    }
  );
};

export const savePersonalAttributeGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/personal-attribute-grade/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save personal attribute grades");
    }
  );
};

export const getPersonalAttributeGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/personal-attribute-grade/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch personal attribute grades");
    }
  );
};

export const updatePersonalAttributeGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/personal-attribute-grade/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update personal attribute grades");
    }
  );
};

export const saveReportingFinalGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reporting-final-grade/save`;
  const isMultipart = model instanceof FormData;
  return axios.post(url, model, isMultipart ? getUploadFileHeader() : getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save final grade");
    }
  );
};

export const updateReportingFinalGrade = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reporting-final-grade/update`;
  const isMultipart = model instanceof FormData;
  return axios.post(url, model, isMultipart ? getUploadFileHeader() : getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update final grade");
    }
  );
};

export const saveReviewingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reviewing-assessment/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save reviewing assessment");
    }
  );
};

export const getReviewingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reviewing-assessment/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch reviewing assessment");
    }
  );
};

export const updateReviewingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/reviewing-assessment/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update reviewing assessment");
    }
  );
};

export const saveAcceptingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/accepting-assessment/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save accepting assessment");
    }
  );
};

export const getAcceptingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/accepting-assessment/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch accepting assessment");
    }
  );
};

export const getAparUserList = (page: number, limit: number, search: string = "") => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/user/list?p=${page}&l=${limit}${search ? `&search=${encodeURIComponent(search)}` : ""}`;
  return axios.get(url, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch user list");
    }
  );
};

export const updateAcceptingAssessment = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/accepting-assessment/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update accepting assessment");
    }
  );
};

export const saveEmployeeAbsence = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-absence/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save employee absence");
    }
  );
};

export const getEmployeeAbsence = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-absence/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch employee absence");
    }
  );
};

export const updateEmployeeAbsence = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-absence/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update employee absence");
    }
  );
};

export const saveEmployeeQualificationTraining = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-qualification-training/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save qualification & training");
    }
  );
};

export const getEmployeeQualificationTraining = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-qualification-training/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch qualification & training");
    }
  );
};

export const updateEmployeeQualificationTraining = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-qualification-training/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update qualification & training");
    }
  );
};

export const saveEmployeeOtherDetails = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-other-details/save`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to save other details");
    }
  );
};

export const getEmployeeOtherDetails = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-other-details/get`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch other details");
    }
  );
};

export const bulkUploadEmployeeAuthority = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-authority/bulk-upload`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to bulk upload authority mapping");
    }
  );
};

export const bulkUploadEmployeeAbsence = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-absence/bulk-upload`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to bulk upload absence data");
    }
  );
};

export const bulkUploadEmployeeQualificationTraining = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-qualification-training/bulk-upload`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to bulk upload qualification & training data");
    }
  );
};

export const bulkUploadEmployeeOtherDetails = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-other-details/bulk-upload`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to bulk upload other details");
    }
  );
};

export const bulkUploadAcademicServiceDetails = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/academic-service-details/bulk-upload`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to bulk upload academic & service details");
    }
  );
};

export const getAparDashboard = (model: any) => {
  // Not auto-injected: the dashboard passes appraisalCycleId explicitly from its
  // own cycle dropdown (see Home.tsx). The backend resolves the cycle from
  // appraisalCycleId — without it, it falls back to the latest cycle and shows
  // the wrong managers/status.
  const url = `${process.env.REACT_APP_BASE_URL}apar/dashboard`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      console.error("Failed to fetch APAR dashboard:", error);
      return null;
    }
  );
};

export const getEmployeePeriods = (model: any) => {
  // NOT cycle-injected: the dashboard's period dropdown lists the employee's
  // periods (including transfer periods) and each is loaded by its own
  // periodId. Cycle-scoping this would empty the dropdown / drop transfers.
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee/periods`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      console.error("Failed to fetch employee periods:", error);
      return null;
    }
  );
};

export const updateEmployeeOtherDetails = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee-other-details/update`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to update other details");
    }
  );
};

// HR Download Reports APIs
export const getHrEmployeeStatusReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/hr-report/employee-status`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch employee status report");
    }
  );
};

export const getHrManagerGradeReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/hr-report/manager-grades`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch manager grade report");
    }
  );
};

export const getHrManagerStatus = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/hr-report/manager-status`;
  model = withSelectedCycle(model);
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch manager status");
    }
  );
};

export const getEmployeeFullReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/employee/full-report`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch full APAR report");
    }
  );
};

export const getHrEmployeeFullReport = (model: any) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/hr/employee/full-report`;
  return axios.post(url, model, getAuthHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to fetch full APAR report");
    }
  );
};

export const uploadMedicalReport = (model: { selfAppraisalId: string | number; medicalReport: File }) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/self-appraisal/upload-medical-report`;
  const formData = new FormData();
  formData.append("selfAppraisalId", String(model.selfAppraisalId));
  formData.append("medicalReport", model.medicalReport);
  return axios.post(url, formData, getUploadFileHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to upload medical report");
    }
  );
};

export const uploadKpiFile = (model: { kpiReportId: string | number; file: File }) => {
  const url = `${process.env.REACT_APP_BASE_URL}apar/kpi-report/upload-file`;
  const formData = new FormData();
  formData.append("kpiReportId", String(model.kpiReportId));
  formData.append("file", model.file);
  return axios.post(url, formData, getUploadFileHeader()).then(
    (response: any) => response.data,
    (error: any) => {
      alert(error.response?.data?.error || "Failed to upload KPI file");
    }
  );
};

